import { FC, useEffect, useState, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IInquiry } from '../../../models/boardCommunication.model';
import { RootState } from '../../../store/rootReducer';
import _ from 'lodash';
import { CgCloseR } from 'react-icons/cg';
import Button from '../../Button';
import FileUpload from '../../FileUpload';
import Tinymce from '../../Tinymce';
import SelectCampus from '../../SelectCampus';
import { Select } from 'antd';
import { setCurrentCampus } from '../../../store/auth.slice';
import useNotification from '../../../hooks/useNotification';
import useAuth from '../../../hooks/useAuth';

interface IProps {
  inquiry?: IInquiry|undefined;
  onSubmit: Function;
}

const BoardNoticeEditor: FC<IProps> = ({ inquiry, onSubmit }) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { infoNotification } = useNotification();
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // BR0001 = 소통창구 구분
  const BR0001 = commonCodes.find(item => item.commonCode === 'BR0001')?.detailCode;

  const [title, setTitle] = useState('');
  const [type, setType] = useState<any>(null);
  const [content, setContent] = useState<any>('');
  // 신규 첨부파일
  const [files, setFiles] = useState<any>([null]);
  // 수정 시 기존 첨부파일
  const [fileinfo, setFileinfo] = useState<any>([null]);

  const titleInput = useRef<any>();

  useEffect(() => {
    if (inquiry) {
      setTitle(inquiry.title);
      dispatch(setCurrentCampus(inquiry.campusCode));
      setType(inquiry.communicationDiv);
      setContent(inquiry.content);
      setFileinfo(_.cloneDeep(inquiry.questionFiles));
    }
  }, [inquiry]);

  const onAddFile = () => {
    const _files = [...files];
    _files.push('');
    setFiles(_files);
  }

  const onDeleteFile = (index: number) => {
    const _files = [...files];
    _files.splice(index, 1);
    setFiles(_files);
  }

  const onSetFile = (index: number, file: any) => {
    const _files = [...files];
    _files[index] = file;
    setFiles(_files);
  }

  const onSave = async () => {
    if (!title) {
      titleInput.current.focus();
      return infoNotification(t('student.student.message15'));
    }
    if (!type) {
      return infoNotification(t('board.inquiry.message3'));
    }

    const form = new FormData();
    form.append('title', title);
    form.append('content', content);
    form.append('communicationDiv', type);
    form.append('campusCode', currentCampus);

    if (inquiry) {
      // 수정 시 파일 처리
      if (inquiry && inquiry.questionFileCode) {
        form.append('questionFileCode', inquiry.questionFileCode);
      }
      if (fileinfo) {
        // 기존 첨부파일 있음
        let hasNewFiles = false;
        files.forEach((item: any) => {
          if (item) hasNewFiles = true;
        });
        if (hasNewFiles) {
          // 추가 첨부파일 있음
          form.append('questionFileStatus', 'update');
        } else {
          // 추가 첨부파일 없음
          let isAllDeleted = true;
          let hasDeleted = false;
          fileinfo.forEach((item: any) => {
            if (item.originalName) {
              isAllDeleted = false;
            } else {
              hasDeleted = true;
              form.append('questionDeleteFiles', item.fileSeq);
            }
          });
          if (isAllDeleted) form.append('questionFileStatus', 'delete');
          else if (hasDeleted) form.append('questionFileStatus', 'update');
          else form.append('questionFileStatus', 'keep');
        }
      } else {
        // 기존 첨부파일 없음
        if (files.length > 0 && files[0] !== null) {
          // 추가 첨부파일 있음
          form.append('questionFileStatus', 'create');
        } else {
          // 추가 첨부파일 없음
          form.append('questionFileStatus', 'keep');
        }
      }

      if (files.length > 0) {
        files.forEach((item: any) => {
          if (item !== null) form.append('questionNewFiles', item);
        });
      }
    } else {
    // 생성 시 파일 처리
      if (files.length > 0) {
        files.forEach((item: any) => {
          if (item !== null) form.append('files', item);
        });
      }
    }

    onSubmit(form);
  }

  const onDeleteAttachedFile = (index: number) => {
    const _fileinfo = [...fileinfo];
    _fileinfo[index].originalName = null;
    setFileinfo(_fileinfo);
  }

  const onSetType = (value: string) => {
    setType(value);
    if (value === 'C' && content === '') {
      setContent(`
        <div>
          ・${t('board.inquiry.text1')}<br /><br />
          ・${t('board.inquiry.text2')}<br /><br />
          ・${t('board.inquiry.text3')}<br /><br />
          ・${t('board.inquiry.text4')}<br /><br />
        </div>
      `);
    }
  }

  return (
    <div className="comm-table__area rowType edit mt-0">
      <table>
        <colgroup>
          <col width="15%" />
          <col width="85%" />
        </colgroup>
        <tbody>
          <tr>
            <th>{`* ${t('common.column.title')}`}</th>
            <td>
              <input
                type="text"
                className="form-control input-text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                ref={titleInput}
              />
            </td>
          </tr>
          <tr>
            <th>{`* ${t('common.menu.campus')}`}</th>
            <td className="flex" colSpan={3}>
              <div className="w-150p">
                <SelectCampus />
              </div>
            </td>
          </tr>
          <tr>
            <th>{`* ${t('board.inquiry.column1')}`}</th>
            <td className="flex" colSpan={3}>
              <div className="w-150p">
                <Select
                  className="w-100"
                  placeholder={t('board.inquiry.column1')}
                  value={type}
                  onChange={value => onSetType(value)}
                >
                  {BR0001?.map(item => (
                    <Select.Option
                      key={item.detailCode}
                      value={item.detailCode}
                    >
                      {item.korName}
                    </Select.Option>
                  ))}
                </Select>                
              </div>
            </td>
          </tr>

          <tr>
            <th>{t('common.column.content')}</th>
            <td>
              <Tinymce
                content={content}
                setContent={setContent}
              />
            </td>
          </tr>
          {
            inquiry && fileinfo
              ? (
                <tr>
                  <th>{t('archive.learn.column5')}</th>
                  <td colSpan={3}>
                    {
                      fileinfo.map((item: any, index: number) => {
                        const result = item?.originalName
                          ? (
                            <div>
                              <a
                                className="download-link"
                                href={`${item.fileLocation}`}
                                download={item.originalName}
                              >
                                {item.originalName}
                              </a>
                              <span
                                className="ml-1 pointer"
                                onClick={() => onDeleteAttachedFile(index)}
                              >
                                <CgCloseR />
                              </span>
                            </div>
                          )
                          : null;
                        return result;
                      })
                    }
                  </td>
                </tr>
                )
              : null
          }
          <tr>
            <th>{inquiry && fileinfo ? t('archive.learn.column6') : t('archive.exam.column7')}</th>
            <td colSpan={3}>
              {
                files.length > 0
                  ? files.map((file: any, index: number) => (
                      <div className="flex mb-2" key={index}>
                        <FileUpload
                          file={files[index]}
                          setFile={(file: any) => onSetFile(index, file)}
                        />
                        <div className="comm-btn__area">
                          <Button
                          className="btn-basic small gray ml-2"
                          onClick={() => onDeleteFile(index)}
                          text={t('common.button.delete')}
                          />
                        </div>
                      </div>
                    ))
                  : null
              }
              <div className="comm-btn__area">
                <Button
                  className="btn-basic pink-line"
                  onClick={onAddFile}
                  text={t('common.button.add')}
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div className="comm-bx__space">
        <div className="comm-btn__area  right">
          <Button
            onClick={onSave}
            className="btn-basic pink"
            text={t('common.button.save')}
            writeAuthYn={writeAuthYn}
          />
          <Button
            onClick={() => history.push(`/board/inquiry${location.search}`)}
            className="btn-basic gray"
            text={t('common.button.cancel')}
          />
        </div>
      </div>
    </div>
  )
}

export default BoardNoticeEditor;
